import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Plus, Trash2, Pencil, X, Save } from "lucide-react";
import { toast } from "sonner";

export function FAQAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => setItems((await api.get("/faqs")).data);
  useEffect(()=>{load();}, []);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/faqs/${edit.id}`, edit); else await api.post("/faqs", edit);
      toast.success("Enregistré"); setEdit(null); load();
    } catch { toast.error("Erreur"); }
  };
  return (
    <div className="p-6 md:p-10" data-testid="admin-faq-page">
      <header className="flex items-center justify-between mb-8">
        <div><div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">SEO longue traîne</div><h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">FAQ</h1></div>
        <Button onClick={()=>setEdit({question:"",answer:"",category:"general",sort_order:0,linked_service:"",linked_zone:""})} className="bg-[color:var(--brand-red)]"><Plus className="w-4 h-4 mr-1"/>Nouvelle</Button>
      </header>
      <div className="space-y-2">
        {items.map(f => (
          <Card key={f.id} className="p-4 border">
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="text-xs text-[color:var(--brand-sky)] font-bold uppercase">{f.category}</div>
                <div className="font-display font-semibold mt-1">{f.question}</div>
                <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{f.answer}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm" onClick={()=>setEdit(f)}><Pencil className="w-3 h-3"/></Button>
                <Button variant="outline" size="sm" onClick={async()=>{await api.delete(`/faqs/${f.id}`); load();}}><Trash2 className="w-3 h-3"/></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b"><h3 className="font-display font-bold text-xl">FAQ</h3><button onClick={()=>setEdit(null)}><X className="w-5 h-5"/></button></div>
            <div className="p-5 space-y-3">
              <div><Label>Question</Label><Input value={edit.question} onChange={e=>setEdit({...edit, question: e.target.value})}/></div>
              <div><Label>Réponse</Label><Textarea rows={5} value={edit.answer} onChange={e=>setEdit({...edit, answer: e.target.value})}/></div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Catégorie</Label><Input value={edit.category} onChange={e=>setEdit({...edit, category: e.target.value})}/></div>
                <div><Label>Ordre</Label><Input type="number" value={edit.sort_order} onChange={e=>setEdit({...edit, sort_order: +e.target.value})}/></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t"><Button variant="outline" onClick={()=>setEdit(null)}>Annuler</Button><Button onClick={save} className="bg-[color:var(--brand-navy)]"><Save className="w-4 h-4 mr-1"/>Enregistrer</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ZonesAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => setItems((await api.get("/zones")).data);
  useEffect(()=>{load();}, []);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/zones/${edit.id}`, edit); else await api.post("/zones", edit);
      toast.success("Enregistré"); setEdit(null); load();
    } catch { toast.error("Erreur"); }
  };
  return (
    <div className="p-6 md:p-10" data-testid="admin-zones-page">
      <header className="flex items-center justify-between mb-8">
        <div><div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">SEO local</div><h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Zones desservies</h1></div>
        <Button onClick={()=>setEdit({city:"",postal_code:"",slug:"",meta_title:"",meta_description:"",content:"",featured:false})} className="bg-[color:var(--brand-red)]"><Plus className="w-4 h-4 mr-1"/>Nouvelle zone</Button>
      </header>
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map(z => (
          <Card key={z.id} className="p-4 border">
            <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{z.postal_code}</div>
            <div className="font-display font-bold text-[color:var(--brand-navy)]">{z.city}</div>
            <div className="flex gap-1 mt-2">
              <Button variant="outline" size="sm" onClick={()=>setEdit(z)}><Pencil className="w-3 h-3"/></Button>
              <Button variant="outline" size="sm" onClick={async()=>{await api.delete(`/zones/${z.id}`); load();}}><Trash2 className="w-3 h-3"/></Button>
            </div>
          </Card>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b"><h3 className="font-display font-bold text-xl">Zone</h3><button onClick={()=>setEdit(null)}><X className="w-5 h-5"/></button></div>
            <div className="p-5 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Ville *</Label><Input value={edit.city} onChange={e=>setEdit({...edit, city: e.target.value})}/></div>
                <div><Label>Code postal *</Label><Input value={edit.postal_code} onChange={e=>setEdit({...edit, postal_code: e.target.value})}/></div>
              </div>
              <div><Label>Meta title</Label><Input value={edit.meta_title} onChange={e=>setEdit({...edit, meta_title: e.target.value})}/></div>
              <div><Label>Meta description</Label><Textarea rows={2} value={edit.meta_description} onChange={e=>setEdit({...edit, meta_description: e.target.value})}/></div>
              <div><Label>Contenu SEO local</Label><Textarea rows={5} value={edit.content} onChange={e=>setEdit({...edit, content: e.target.value})}/></div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t"><Button variant="outline" onClick={()=>setEdit(null)}>Annuler</Button><Button onClick={save} className="bg-[color:var(--brand-navy)]"><Save className="w-4 h-4 mr-1"/>Enregistrer</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}
